<script setup lang="ts">
import { Plus, EditPencil, Trash, Bank } from "@iconoir/vue";
import { useList } from "~/composables/use-list";
import { useCrud } from "~/composables/use-crud";
import { useFormat } from "~/composables/use-format";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import CostEntryFormModal from "./CostEntryFormModal.vue";
import CashBookModal from "./CashBookModal.vue";
import { AdminPermission } from "~~/schemas/admin-role";

defineOptions({ name: "CostEntry" });

const { t } = useI18n();
const { c, d } = useFormat();


interface CostEntry {
  id: string;
  category: string;
  vendorName: string;
  description: string;
  amount: number;
  currency: string;
  periodMonth: string;
  status: string;
  paidTime: number;
  source: string;
  sourceKey: string;
}

const defaultFilter = {
  keyword: "",
  category: "",
  source: "",
  page: 1,
  pageSize: 20,
};

const {
  firstLoading,
  listLoading,
  load,
  itemCount,
  pageCount,
  items,
  filter,
  isEmpty,
  isSearchEmpty,
  search,
  searchDebounced,
} = useList<CostEntry, typeof defaultFilter>({
  endpoint: "/cost-entries",
  defaultFilter,
});

const { del, errorMsg } = useCrud<CostEntry>({ endpoint: "/cost-entries" });

const formRef = ref<InstanceType<typeof CostEntryFormModal> | null>(null);
const cashBookRef = ref<InstanceType<typeof CashBookModal> | null>(null);

const categoryOptions = computed(() => [
  { value: "", label: t("finance.cost.categoryAll") },
  ...["payroll", "infrastructure", "software", "marketing", "sales", "operations", "office", "tax", "other"].map((value) => ({
    value,
    label: t(`finance.cost.category_${value}`),
  })),
]);

const sourceOptions = computed(() => [
  { value: "", label: t("finance.cost.source") },
  { value: "manual", label: t("finance.cost.sourceManual") },
  { value: "collector", label: t("finance.cost.sourceCollector") },
]);

function isManual(row: CostEntry) {
  return row.source !== "collector";
}
// "2026-08" is a storage key, not something to read in a table next to a
// formatted date — same order as every other date on the screen.
function monthLabel(month: string) {
  return month ? `${month.slice(5)}/${month.slice(0, 4)}` : "—";
}

// Joined with a middle dot, matching the plan order list.
function costSecondary(row: any) {
  return isManual(row)
    ? row.description
    : t("finance.cost.collectorReadOnly", { key: row.sourceKey || t("finance.cost.sourceCollector") });
}
const toast = useToast();
const alert = useAlert();
const { can } = useAccess();
const { set: setTitle } = usePageTitle();

async function confirmDelete(row: CostEntry) {
  const ok = await alert.negative({
    title: t("finance.cost.deleteConfirmTitle"),
    description: t("finance.cost.deleteConfirmBody", { name: row.vendorName, month: row.periodMonth }),
    confirmLabel: t("action.delete"),
    cancelLabel: t("action.cancel"),
  });
  if (!ok) return;

  try {
    await del(row.id);
    toast.success({ title: t("message.deleteSuccess") });
    const nextItemCount = Math.max(0, itemCount.value - 1);
    const nextPageCount = Math.max(1, Math.ceil(nextItemCount / filter.pageSize));
    if (Number(filter.page) > nextPageCount) {
      filter.page = nextPageCount;
    }
    await load();
  } catch {
    if (errorMsg.value) toast.error({ title: errorMsg.value });
  }
}

onMounted(async () => {
  setTitle(t("finance.costsTitle"));
  await load();
});

watch(
  () => filter.keyword,
  () => {
    searchDebounced();
  },
);

watch(
  () => [filter.category, filter.source],
  () => {
    search();
  },
);

watch(
  () => filter.page,
  () => {
    load();
  },
);
</script>

<template>
  <ListPage
    :title="t('finance.costsTitle')"
    :first-loading="firstLoading"
    :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty"
  >
    <template #status>
      {{ listLoading ? t("ui.loading") : `${itemCount} ${t('finance.cost.itemsSuffix')}` }}
    </template>

    <template #actions>
      <Button
        v-if="can(AdminPermission.CostsWrite)"
        variant="secondary"
        size="small"
        @click="cashBookRef?.open()"
      >
        <Bank class="icon" />
        {{ t("finance.cashBooks") }}
      </Button>
      <Button
        v-if="can(AdminPermission.CostsWrite)"
        variant="primary"
        size="small"
        @click="formRef?.open()"
      >
        <Plus class="icon" />
        {{ t("finance.cost.add") }}
      </Button>
    </template>

    <template #empty>
      <ListEmpty
        :title="t('finance.cost.emptyTitle')"
        :subtitle="t('finance.cost.emptySubtitle')"
      >
        <template #action>
          <Button
            v-if="can(AdminPermission.CostsWrite)"
            variant="primary"
            size="small"
            @click="formRef?.open()"
          >
            <Plus class="icon" />
            {{ t("finance.cost.add") }}
          </Button>
        </template>
      </ListEmpty>
    </template>

    <template #search-empty>
      <ListEmpty
        :title="t('finance.cost.emptySearchTitle')"
        :subtitle="t('finance.cost.emptySearchSubtitle')"
      />
    </template>

    <template #filter-left>
      <FormSelect v-model="filter.category" :options="categoryOptions" size="sm" class="w-full md:flex-1" />
      <FormSelect v-model="filter.source" :options="sourceOptions" size="sm" class="w-full md:flex-1" />
    </template>

    <template #filter-right>
      <FormSearch
        v-model="filter.keyword"
        :placeholder="t('field.search')"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #default>
      <DataTable
        :columns="[
          { key: 'vendorName', label: t('finance.cost.vendor'), class: 'md:min-w-[240px]' },
          { key: 'category', label: t('finance.cost.category'), class: 'min-w-[128px] hidden md:table-cell' },
          { key: 'status', label: t('field.status'), class: 'min-w-[112px]' },
          { key: 'amount', label: t('finance.cost.amount'), align: 'right', class: 'min-w-[128px]' },
          { key: 'periodMonth', label: t('finance.cost.month'), align: 'right', class: 'min-w-[112px]' },
          { key: 'paidTime', label: t('finance.cost.paidTime'), align: 'right', class: 'min-w-[128px] hidden lg:table-cell' },
          { key: 'actions', align: 'right', label: t('ui.action'), class: 'min-w-[112px]' },
        ]"
        :items="items"
      >
        <template #cell-vendorName="{ row }">
          <div class="flex min-w-0 flex-col gap-3xsmall">
            <span class="content-neutral-strong truncate">{{ row.vendorName }}</span>
            <!-- What the cost was for. A collector row says where it came
                 from instead — it is read-only, and an empty actions column
                 alone does not explain why. Category has its own badge column,
                 which is hidden on mobile, so this line never carries it.
                 Title carries the full text past the truncation. -->
            <span
              class="text-body-small-tertiary content-neutral-medium truncate"
              :title="costSecondary(row)"
            >
              {{ costSecondary(row) }}
            </span>
          </div>
        </template>

        <template #cell-category="{ row }">
          <Badge intent="neutral" emphasis="medium" size="small">
            {{ t(`finance.cost.category_${row.category}`) }}
          </Badge>
        </template>

        <template #cell-status="{ row }">
          <Badge :intent="row.status === 'paid' ? 'positive' : 'neutral'" emphasis="medium" size="small">
            {{ t(row.status === "paid" ? "finance.cost.statusPaid" : "finance.cost.statusPlanned") }}
          </Badge>
        </template>

        <template #cell-amount="{ row }">
          <span class="cost-entry__figure content-neutral-strong">{{ c(row.amount, row.currency) }}</span>
        </template>

        <template #cell-periodMonth="{ row }">
          <span class="cost-entry__figure">{{ monthLabel(row.periodMonth) }}</span>
        </template>

        <!-- Only a paid entry has one; a planned one shows a dash rather than
             borrowing the accounting month and reading as already settled. -->
        <template #cell-paidTime="{ row }">
          <span class="cost-entry__figure" :class="{ 'content-neutral-medium': !row.paidTime }">
            {{ row.paidTime ? d(row.paidTime, "short") : "—" }}
          </span>
        </template>

        <template #actions="{ row }">
          <div v-if="isManual(row)" class="flex items-center justify-end gap-xsmall">
            <ButtonIcon
              v-if="can(AdminPermission.CostsWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('action.edit')"
              :aria-label="t('action.edit')"
              @click.stop="formRef?.open(row.id)"
            >
              <EditPencil class="icon" />
            </ButtonIcon>
            <ButtonIcon
              v-if="can(AdminPermission.CostsWrite)"
              variant="neutral-flat"
              size="small"
              :title="t('action.delete')"
              :aria-label="t('action.delete')"
              @click.stop="confirmDelete(row)"
            >
              <Trash class="icon" />
            </ButtonIcon>
          </div>
        </template>
      </DataTable>

      <Pagination
        v-if="pageCount > 1"
        v-model:page="filter.page"
        :count="itemCount"
        :page-size="filter.pageSize"
        class="mt-medium"
      />
    </template>
  </ListPage>

  <CostEntryFormModal ref="formRef" @saved="load()" />
  <CashBookModal ref="cashBookRef" />
</template>

<style scoped lang="scss">
// Amounts and months line up digit for digit down their columns.
.cost-entry__figure {
  font-variant-numeric: tabular-nums;
}
</style>
